import { Button } from '@mui/material';
import { DocumentStatus, DocumentType } from '@synergy/models';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { Localized } from '@synergy/components';
import { PaloColumn, PaloListView, PaloRowAction, PaloStatusPill, usePaloListSort } from '@synergy/components-palo';
import { ReactElement, useEffect, useState } from 'react';
import { UrlFilter } from '@synergy/components';
import { getDocumentStatusTranslationId } from '@synergy/utils';
import { useLocation, useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import * as DemandActions from '../demand-actions';
import { ContactPresenter } from '../../../common/components/contact-presenter/contact-presenter';
import { ConverterReceiptModal } from '../../convert-receipt/converter-receipt-modal';
import { Demand } from '../models/demand';
import { DemandTransition } from '../../convert-receipt/models/demand/demandTransition';
import { PaloDemandOverviewFilter } from './palo-demand-overview-filter';
import { PaloDocumentNumberCell } from '../../../common/components/palo/palo-document-number-cell';
import { PaloNamedFilterChips } from '../../../common/components/palo/palo-named-filter-chips';
import { PaloResponseCountCell } from './palo-response-count-cell';
import { Transition } from '../../convert-receipt/models/type-transition';
import { TransitionTarget } from '../../convert-receipt/models/internal/transition-target';
import { fetchDemandTransitionData } from '../containers/detail/store/demand-detail-actions';
import { fetchTransitions, resetTransitions } from '../../../common/actions/transitions-activity-actions';
import { transitionDataSelector } from '../containers/detail/store/demand-detail-selectors';
import {
    transitionDemandIntoInquiries,
    transitionDemandIntoPurchaseOrders,
} from '../../convert-receipt/transition-actions';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { usePaloColumnFilters } from '../../../common/components/palo/use-palo-column-filters';
import { usePaloListViews } from '../../../common/components/palo/use-palo-list-views';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

const columns: PaloColumn<Demand>[] = [
    {
        key: 'number',
        type: 'documentNumber',
        sortKey: 'number',
        head: <FormattedMessage id="app.table.column.number" />,
        cell: demand => <PaloDocumentNumberCell number={demand.number} alert={demand.receiptAlert} />,
    },
    {
        key: 'status',
        type: 'status',
        sortKey: 'status',
        head: <FormattedMessage id="app.table.column.status" />,
        // No filter on the column itself: the same filter in two places wrote two
        // different keys into the address and answered each other (LST-S9, GF
        // 13.10.2026). The navigation on the left and the filter dialog set it in
        // one key (#2340); the active one stands as a chip over the list.
        cell: demand => (
            <PaloStatusPill
                status={demand.status}
                label={<FormattedMessage id={getDocumentStatusTranslationId(demand.status)} />}
            />
        ),
    },
    {
        // Members with a need of those invited (GF 30.10.2026, #2340).
        key: 'responses',
        sortKey: 'responses',
        type: 'count',
        head: <FormattedMessage id="app.table.column.responses" />,
        cell: demand => <PaloResponseCountCell count={demand.responseCount} />,
    },
    {
        key: 'title',
        type: 'name',
        sortKey: 'title',
        head: <FormattedMessage id="app.table.column.title" />,
        cell: demand => <Localized value={demand.title} />,
    },
    {
        key: 'responsibleContact',
        type: 'name',
        sortKey: 'responsibleContact',
        head: <FormattedMessage id="app.common.responsibleContact" />,
        cell: demand => (
            <ContactPresenter
                person={demand.responsibleContact}
                fullName={demand.responsibleContact?.fullName}
                isDeleted={demand.responsibleContact?.isDeleted}
                isLocked={demand.responsibleContact?.isLocked}
            />
        ),
    },
    {
        key: 'createdAt',
        type: 'date',
        sortKey: 'createdAt',
        head: <FormattedMessage id="app.table.column.createdAt" />,
        cell: demand => <FormattedDate value={demand.createdAt} year="numeric" month="short" day="2-digit" />,
    },
];

export const PaloDemandOverview = (): ReactElement => {
    const { hiddenColumnKeys, shownColumnKeys, toggleColumn, moveColumn, orderColumns, savedFiltersMenu } =
        usePaloListViews('demands');
    const columnFilter = usePaloColumnFilters('demands');
    const dispatch = useThunkDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { sort, params } = usePaloListSort('demands');
    const intl = useIntl();
    const { flat: paginated, requesting } = useAppSelector(state => state.demands);
    const transitionData = useAppSelector(transitionDataSelector);

    const page = Number(searchParams.get('page') ?? 1); // 1-based
    const limit = Number(searchParams.get('limit') ?? 50);

    useEffect(() => {
        dispatch(DemandActions.fetchDemands(params));
    }, [dispatch, params]);

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const detailHref = (demand: Demand): string => location.pathname + '/' + demand.id;

    const openDetail = (demand: Demand): void => navigate(detailHref(demand));

    const headerActions = (
        <Button variant="contained" color="primary" onClick={() => navigate(location.pathname + '/new')}>
            <FormattedMessage id="app.demands.new" />
        </Button>
    );

    const [isConverterVisible, setConverterVisible] = useState(false);

    const handleConvertOpen = (id: number): void => {
        dispatch(DemandActions.resetDemand());
        dispatch(resetTransitions());
        dispatch(fetchTransitions(DocumentType.Demand, id));
        dispatch(fetchDemandTransitionData(id));
        setConverterVisible(true);
    };

    const handleConvertClose = (): void => {
        dispatch(DemandActions.requestEnd());
        dispatch(DemandActions.resetDemands());
        setConverterVisible(false);
    };

    const handleTransition = (transitionObject: Transition, target: TransitionTarget) =>
        target === TransitionTarget.PurchaseOrder
            ? dispatch(transitionDemandIntoPurchaseOrders(transitionObject as DemandTransition))
            : dispatch(transitionDemandIntoInquiries(transitionObject as DemandTransition));

    const rowActions = (demand: Demand): PaloRowAction[] => [
        { label: 'Öffnen', onClick: () => openDetail(demand) },
        { label: 'Umwandeln', onClick: () => handleConvertOpen(demand.id) },
        { label: 'Duplizieren', onClick: () => dispatch(DemandActions.duplicateDemand(demand.id)) },
        { label: 'Abbrechen', onClick: () => dispatch(DemandActions.abortScheduledDemand(demand.id)) },
        { label: 'Löschen', onClick: () => dispatch(DemandActions.deleteDemand(demand.id)) },
    ];

    const handleExport = (): void => {
        const filename = `${intl.formatMessage({ id: 'app.export.demands' })}.csv`;
        dispatch(DemandActions.exportDemands(filename, searchParams));
    };

    return (
        <UrlFilter useLegacyFilter={true}>
            <PaloListView<Demand>
                title={<FormattedMessage id="app.navigation.demands" />}
                pageTitle={intl.formatMessage({ id: 'app.navigation.demands' })}
                onExport={handleExport}
                filters={
                    <>
                        <PaloDemandOverviewFilter />
                        {savedFiltersMenu}
                    </>
                }
                filterChips={<PaloNamedFilterChips listKey="demands" />}
                sort={sort}
                onSortChange={(key, order) => updateParams({ sort: key, order, page: '1' })}
                searchValue={searchParams.get('search') ?? ''}
                searchPlaceholder="Titel, Mitglied, Artikel …"
                onSearchChange={value => {
                    const next = new URLSearchParams(searchParams);
                    if (value) {
                        next.set('search', value);
                    } else {
                        next.delete('search');
                    }
                    next.set('page', '1');
                    setSearchParams(next, { replace: true });
                }}
                headerActions={headerActions}
                columns={orderColumns(columns)}
                columnFilter={columnFilter}
                onMoveColumn={moveColumn}
                hiddenColumnKeys={hiddenColumnKeys}
                shownColumnKeys={shownColumnKeys}
                onToggleColumn={toggleColumn}
                items={paginated.items}
                getRowId={demand => demand.id}
                requesting={requesting}
                emptyLabel="Kein Bedarf."
                rowHref={detailHref}
                rowActions={rowActions}
                pagination={{
                    count: paginated.total,
                    page,
                    limit,
                    onPageChange: newPage => updateParams({ page: String(newPage) }),
                    onLimitChange: newLimit => updateParams({ limit: String(newLimit), page: '1' }),
                }}
            >
                {transitionData && (
                    <ConverterReceiptModal
                        isVisible={isConverterVisible}
                        source={transitionData}
                        onClose={handleConvertClose}
                        onConvert={handleTransition}
                    />
                )}
            </PaloListView>
        </UrlFilter>
    );
};
